import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import {
  parseAzureError,
  ParsedAzureError,
} from '@/utils/errors/parseAzureError';

interface WorkspaceError {
  error: Error;
  errorInfo?: React.ErrorInfo;
  timestamp: Date;
  workspaceId?: string;
  azureError?: ParsedAzureError;
  response?: unknown; // Store the response if available
}

interface WorkspaceErrorContextType {
  errors: WorkspaceError[];
  addError: (
    error: Error,
    errorInfo?: React.ErrorInfo,
    workspaceId?: string,
    response?: unknown,
  ) => void;
  clearErrors: () => void;
  clearError: (index: number) => void;
  hasErrors: boolean;
}

const WorkspaceErrorContext = createContext<
  WorkspaceErrorContextType | undefined
>(undefined);

export const useWorkspaceError = () => {
  const context = useContext(WorkspaceErrorContext);
  if (!context) {
    throw new Error(
      'useWorkspaceError must be used within a WorkspaceErrorProvider',
    );
  }
  return context;
};

// Safe hook that returns undefined if context is not available (for use in ErrorBoundary)
export const useWorkspaceErrorSafe = ():
  | WorkspaceErrorContextType
  | undefined => {
  return useContext(WorkspaceErrorContext);
};

interface WorkspaceErrorProviderProps {
  children: ReactNode;
}

export const WorkspaceErrorProvider: React.FC<WorkspaceErrorProviderProps> = ({
  children,
}) => {
  const [errors, setErrors] = useState<WorkspaceError[]>([]);

  const addError = useCallback(
    (
      error: Error,
      errorInfo?: React.ErrorInfo,
      workspaceId?: string,
      response?: unknown,
    ) => {
      // Parse Azure errors from the error message or response
      let azureError: ParsedAzureError = parseAzureError(error);

      // First, try to parse from error message
      // If no Azure error found in message, check response if available
      if (!azureError.isAzureError && response != null) {
        try {
          // Try to extract error from response
          const responseStr =
            typeof response === 'string' ? response : JSON.stringify(response);
          azureError = parseAzureError(responseStr);
        } catch {
          // If parsing fails, ignore
        }
      }

      // Also check error.stack for Azure errors
      if (!azureError.isAzureError && error.stack) {
        azureError = parseAzureError(error.stack);
      }

      const newError: WorkspaceError = {
        error,
        errorInfo,
        timestamp: new Date(),
        workspaceId,
        azureError: azureError.isAzureError ? azureError : undefined,
        response,
      };
      setErrors((prev) => [...prev, newError]);
      console.error('Workspace error added:', newError);
    },
    [],
  );

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const clearError = useCallback((index: number) => {
    setErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const value: WorkspaceErrorContextType = {
    errors,
    addError,
    clearErrors,
    clearError,
    hasErrors: errors.length > 0,
  };

  return (
    <WorkspaceErrorContext.Provider value={value}>
      {children}
    </WorkspaceErrorContext.Provider>
  );
};
